import { useLazyQuery } from '@apollo/client';
import { useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { ClipboardCopy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PROFILE_DOCUMENT_DEFAULTS } from './queries';
import { profileDocumentDefaultValues, profileDocumentObject, type ProfileDocumentNode } from './profileDocumentPresentation';
import type { ProfileValue } from './types';

const rootKeys = (nodes: ProfileDocumentNode[]): string[] => nodes.flatMap(node => node.kind === 'group' ? rootKeys(node.children) : [node.key]);
const validValues = (value: unknown): value is ProfileValue[] => Array.isArray(value)
  && value.every(field => field && typeof field === 'object' && typeof field.name === 'string' && (field.value === null || typeof field.value === 'string'));

export const ProfileDocumentDefaults = ({ odrnumber, form, nodes, disabled, onBusy }: {
  odrnumber: number; form: UseFormReturn<Record<string, string>>; nodes: ProfileDocumentNode[]; disabled: boolean; onBusy: (busy: boolean) => void;
}) => {
  const [load] = useLazyQuery<{ participantProfileDocumentDefaults?: { values?: unknown } | null }>(PROFILE_DOCUMENT_DEFAULTS, { fetchPolicy: 'network-only' });
  const [busy, setBusy] = useState(false); const pending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const apply = async () => {
    if (pending.current || disabled) return;
    const type = form.getValues('TYPE');
    pending.current = true; setBusy(true); onBusy(true); setError(null); setMessage(null);
    try {
      const result = await load({ variables: { odrnumber } });
      const values = result.data?.participantProfileDocumentDefaults?.values;
      if (result.error || !validValues(values)) throw new Error('Current profile details are unavailable.');
      if (form.getValues('TYPE') !== type) throw new Error('The document type changed. Review it before copying details.');
      const current = profileDocumentObject(form.getValues('contents'));
      if (!current) throw new Error('Review the recorded document contents before copying details.');
      const keys = new Set(rootKeys(nodes));
      const updates = Object.entries(profileDocumentDefaultValues(type, values)).filter(([name, value]) => keys.has(name) && value
        && (current[name] == null || current[name] === ''));
      if (updates.length) form.setValue('contents', JSON.stringify({ ...current, ...Object.fromEntries(updates) }), { shouldDirty: true, shouldValidate: true });
      setMessage(updates.length ? 'Current profile details were copied into empty fields. Review them before saving.' : 'No empty document fields could be filled from the permitted profile details.');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to copy current profile details.');
    } finally { pending.current = false; setBusy(false); onBusy(false); }
  };
  return <div className="space-y-2">
    <Button id="profile-document-copy-details" type="button" variant="outline" disabled={disabled || busy}
      onClick={() => { void apply(); }}><ClipboardCopy aria-hidden="true" className="mr-2 h-4 w-4" />Use current profile details</Button>
    {busy && <p role="status">Reading current profile details…</p>}
    {error && <p role="alert" className="text-destructive">{error}</p>}
    {message && <p role="status">{message}</p>}
  </div>;
};
